import {useContext, useRef} from "react";
import {useLocation as useReactRouterLocation} from "react-router-dom-v5-compat";

import {Errors, KAError} from "@khan/logging";
import {InitialRequestUrl} from "@khan/shared-core";
import {AppShellContext} from "@khan/shared-core/context";

export interface Location {
    href: string;
    protocol: string;
    host: string;
    hostname: string;
    port: string;
    pathname: string;
    search: string;
    hash: string;
    origin: string;
    // TODO(somewhatabstract): We should really return
    // Record<string, string | Array<string>> here but
    // that's hard to dereference. Instead, we should long term return
    // URLSearchParams instance and lint against using the indexer on that
    // since it does not do what one expects and there's no error telling you.
    queryParams: Record<string, string>;
    // State passed through a React Router Redirect
    // https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/Redirect.md#to-object
    state?: any;
}

// Returns the same Location object that withLocation injects as a prop.
// WARNING: This can only be used within the appshell.
export const useLocation = (): Location => {
    const {inAppShell} = useContext(AppShellContext);

    const inAppShellRef = useRef(inAppShell);

    if (inAppShell !== inAppShellRef.current) {
        throw new KAError(
            `inAppShell changed from ${inAppShellRef.current.toString()} to ${inAppShell.toString()}`,
            Errors.Internal,
        );
    }

    // eslint-disable-next-line no-restricted-syntax -- This is safe to use in this context.
    const location = window.location;
    // NOTE(kevinb): We need to avoid calling `useReactRouterLocation` when
    // we're not inside of the app-shell.  This is because there's no router
    // set up in these situations nor would it make sense to add one.  Although
    // this does break the "rules of hooks", the value of `inAppShell` should
    // never change over the course of a hook's lifespan, making this safe.
    const {pathname, search, hash, state} = inAppShell
        ? // eslint-disable-next-line react-hooks/rules-of-hooks -- See above.
          useReactRouterLocation()
        : {
              pathname: location.pathname,
              search: location.search,
              hash: location.hash,
              state: undefined,
          };

    return {
        href: InitialRequestUrl.origin + pathname + search + hash,
        protocol: InitialRequestUrl.protocol,
        host: InitialRequestUrl.host,
        hostname: InitialRequestUrl.hostname,
        port: InitialRequestUrl.port,
        pathname,
        search,
        hash,
        origin: InitialRequestUrl.origin,
        get queryParams() {
            const params = new URLSearchParams(search);
            // NOTE(somewhatabstract): This object is really
            // Record<string, string | Array<string>> but the original
            // function we used (that also built an object like this) had
            // incorrect typing so we're maintaining that for
            // now to make updating simpler.
            const paramsAsObject = Object.fromEntries(params.entries());
            return paramsAsObject;
        },
        state,
    };
};
